import { useCallback, useEffect, useRef, useState } from 'react';

import { messageFrom } from '@bp-files/client';
import { getSignature, type FileSummary } from '@/lib/api';
import { uploadFile } from '@/lib/upload';
import {
  strokesToInkWithBox,
  type RawPoint,
  type SignatureInk,
} from '@/components/Signature/signatureInk';
import { rectFromScreen, type FieldRect, type PageView } from './signField';
import { SIGNING_COPY } from './signing.copy';
import { SignError, signPdf, type SignField, type SignFieldKind } from './signPdf';

/**
 * The strokes drawn on each page while the pencil is armed, keyed by page index,
 * each with the view they were captured against so the mark's box maps back to
 * page points at commit. Zoom is locked during a drawing, so that view holds.
 */
type DrawBuffer = Record<number, { strokes: RawPoint[][]; view: PageView }>;

/**
 * One entry on the local undo history: the whole editable state before an
 * action changed it. Both halves are captured by reference and never mutated in
 * place — `place`/`move`/`setText`/`remove` each build a new `fields` array, and
 * `setDrawStrokes` a new buffer object — so a stored reference stays the value
 * it was. Nothing here is persisted; the stack lives and dies with the tab.
 */
interface EditSnapshot {
  fields: SignField[];
  draw: DrawBuffer;
}

/**
 * The accumulated freehand strokes, as `draw` fields ready to burn.
 *
 * One field per page — a drawing is the strokes made on it. Strokes too faint
 * to store (`strokesToInkWithBox` returns `undefined`) are dropped, so a stray
 * tap on the page makes no field. The box the ink was normalised against maps
 * to the placement rect through the same `rectFromScreen` the overlay uses.
 */
function fieldsFromDrawBuffer(buffer: DrawBuffer): SignField[] {
  const fields: SignField[] = [];

  for (const [key, entry] of Object.entries(buffer)) {
    const marked = strokesToInkWithBox(entry.strokes);
    if (marked === undefined) continue;

    fields.push({
      id: crypto.randomUUID(),
      kind: 'draw',
      pageIndex: Number(key),
      ink: marked.ink,
      rect: rectFromScreen(marked.box, entry.view),
    });
  }

  return fields;
}

/**
 * Everything about signing one document (FIL-8c).
 *
 * **A hook rather than state inside `PdfViewer`**, so that the viewer stays the
 * frame it was: a toolbar, a stage, a footer. Signing adds a stored mark to
 * fetch, a list of placed fields, a save that runs two libraries and an upload,
 * and four ways to fail — none of which the reader needs to know about.
 *
 * **Nothing leaves the tab until Save.** Placing, dragging and typing are all
 * local; the document is produced and uploaded in one act, which is what makes
 * closing without saving a thing that can simply be discarded. It is also why
 * `requestClose` exists: work that was never sent is work that is easy to lose
 * by pressing Escape.
 *
 * **The bytes come from the cache the viewer already filled**, never from a
 * second fetch. ADR-0053 makes every fetch of a file an audited, metered
 * download, so signing a document a reader is looking at must not cost a second
 * `file.downloaded` row under their name.
 */

export interface UseSigningInput {
  file: FileSummary;
  /** The original bytes, from `useFileBlob`'s cache. `undefined` until they land. */
  blob: Blob | undefined;
  spaceId: string;
  folderId: string;
  /** False for a reader who cannot upload — nothing is fetched and nothing renders. */
  enabled: boolean;
  onSigned: () => void;
}

export interface Signing {
  /** Whether the document can be signed at all: write access and a stored mark. */
  signable: boolean;
  /** True when the person may sign but has not set a signature up yet. */
  needsSignature: boolean;
  /**
   * Whether freehand drawing is on offer — write access, and nothing else. The
   * pencil needs no stored signature, so a signer who never set one up can
   * still draw on a document.
   */
  canDraw: boolean;
  ink: SignatureInk | undefined;
  tool: SignFieldKind | undefined;
  fields: SignField[];
  /**
   * Whether there is anything to save — a placed field, or an uncommitted
   * drawing still in the buffer. Save keys on this rather than `fields.length`
   * so a drawing the reader has not switched away from is not lost silently.
   */
  hasContent: boolean;
  /** The freehand strokes drawn on each page, for `DrawLayer` to paint. */
  drawStrokes: DrawBuffer;
  /**
   * Bumped by `undo` so each mounted `DrawLayer` remounts and re-seeds from the
   * restored buffer. The layer owns its strokes after mount (`SignaturePad`'s
   * pattern), so an undone stroke leaves the canvas only when its `key` changes.
   */
  drawGeneration: number;
  saving: boolean;
  error: string | undefined;
  chooseTool: (tool: SignFieldKind | undefined) => void;
  place: (field: SignField) => void;
  /** Records the strokes drawn on one page. `[]` clears that page's buffer. */
  setDrawStrokes: (pageIndex: number, strokes: RawPoint[][], view: PageView) => void;
  move: (id: string, rect: FieldRect) => void;
  setText: (id: string, text: string) => void;
  remove: (id: string) => void;
  /**
   * Closes the current continuous edit — the end of a drag or of a run of
   * typing in one field — so the next one starts a fresh undo step. `FieldLayer`
   * calls it on a drag's `pointerup` and a text field's `blur`. Placing,
   * removing and each finished stroke are already discrete and need no close.
   */
  endEdit: () => void;
  /** Whether there is a local edit to undo. */
  canUndo: boolean;
  /**
   * Undo the last local edit — a placement, a move or resize, a run of typing, a
   * committed drawing, or a delete — in reverse order. Purely local: it rewinds
   * the in-tab state and never touches the server, the same way nothing else
   * here leaves the tab until Save.
   */
  undo: () => void;
  save: () => void;
  /**
   * Leaves sign mode outright: disarms the tool and throws away everything
   * placed, confirming first if anything was.
   *
   * The mobile bottom bar's Cancel, and only that — on desktop, backing out is
   * pressing the armed tool again, which is a toggle the bar has room to keep
   * showing. Deliberately **not** wired to Escape: that key backs out of the
   * innermost thing (`onEscape` below), and giving it the power to destroy
   * placed work would be a new meaning for a key readers already press
   * reflexively.
   */
  discard: () => void;
  /** The name the signed copy will take, so the button can say it before it exists. */
  signedName: string;
  /** Wraps the frame's close so unsaved fields are not lost silently. */
  requestClose: (close: () => void) => () => void;
  /** Escape leaves the armed tool before it closes the reader. */
  onEscape: () => boolean;
}

/**
 * `report.pdf` → `report_signed.pdf`, and `report` → `report_signed`.
 *
 * FIL-8c leaves collisions alone deliberately: signing twice makes two rows
 * distinguished by uploader and timestamp, and signing a signed document makes
 * `report_signed_signed.pdf`. Both are honest; a de-duplicating suffix would
 * invent a claim about which one is authoritative.
 */
export function signedName(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  if (dot < 1) return `${fileName}_signed`;

  return `${fileName.slice(0, dot)}_signed${fileName.slice(dot)}`;
}

export function useSigning({
  file,
  blob,
  spaceId,
  folderId,
  enabled,
  onSigned,
}: UseSigningInput): Signing {
  const [ink, setInk] = useState<SignatureInk | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [tool, setTool] = useState<SignFieldKind | undefined>(undefined);
  const [fields, setFields] = useState<SignField[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const [drawStrokes, setDrawState] = useState<DrawBuffer>({});
  /**
   * A synchronous mirror of the draw buffer, so `commitDrawing` — called from
   * `chooseTool`, `save`, `discard` and `onEscape` — reads what was drawn
   * without waiting for a state flush.
   */
  const drawBuffer = useRef<DrawBuffer>({});
  /** The armed tool as of the last render, read before `setTool` replaces it. */
  const toolRef = useRef(tool);
  useEffect(() => {
    toolRef.current = tool;
  }, [tool]);

  /**
   * Fetch the stored mark once, and only for somebody who could use it.
   *
   * A reader with no write access is never asked about — the route would answer
   * perfectly well, and asking would be a request per document opened for a
   * feature that is not on offer.
   */
  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();

    getSignature(controller.signal)
      .then((body) => {
        if (controller.signal.aborted) return;

        setLoaded(true);
        if (body.signature === null) return;

        try {
          setInk(JSON.parse(body.signature.ink) as SignatureInk);
        } catch {
          // A stored mark this build cannot read leaves signing unavailable
          // rather than crashing the reader. `StoredSignature` takes the same
          // line on the account screen.
        }
      })
      .catch(() => {
        // Signing is an addition to a reader that works without it, so a
        // failure here withholds the control rather than replacing the
        // document with an error.
        if (!controller.signal.aborted) setLoaded(true);
      });

    return () => {
      controller.abort();
    };
  }, [enabled]);

  /**
   * The local undo history — a stack of the editable state before each edit.
   * Kept as state so `canUndo` is reactive; mirrored to a ref so the arm/commit
   * bookkeeping below can read the length synchronously.
   */
  const [undoStack, setUndoStack] = useState<EditSnapshot[]>([]);
  const undoStackRef = useRef<EditSnapshot[]>([]);
  useEffect(() => {
    undoStackRef.current = undoStack;
  }, [undoStack]);
  const [drawGeneration, setDrawGeneration] = useState(0);

  /** A synchronous mirror of `fields`, for the snapshot taken before an edit. */
  const fieldsRef = useRef<SignField[]>(fields);
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  /**
   * The continuous edit in progress, so its many changes fold into one undo
   * step: `move:<id>` for a drag, `text:<id>` for a run of typing, `null` for a
   * discrete edit that never repeats. Reset by `endEdit`.
   */
  const editKey = useRef<string | null>(null);
  /**
   * The undo-stack depth at which the current pencil session began, so its
   * per-stroke steps can collapse to one when the drawing commits.
   */
  const drawSessionBase = useRef<number | null>(null);

  const snapshotBefore = useCallback((key: string | null) => {
    // A continuous edit records the state before its first change; the rest ride
    // along on that one step until `endEdit` (or a different edit) closes it.
    if (key !== null && editKey.current === key) return;
    // Read the state *now*, not inside the updater: `setDrawStrokes` reassigns
    // `drawBuffer.current` synchronously right after this call, so a lazy read
    // would snapshot the post-change buffer instead of the one before it.
    const snapshot: EditSnapshot = { fields: fieldsRef.current, draw: drawBuffer.current };
    setUndoStack((stack) => [...stack, snapshot]);
    editKey.current = key;
  }, []);

  const endEdit = useCallback(() => {
    editKey.current = null;
  }, []);

  const setDrawStrokes = useCallback(
    (pageIndex: number, strokes: RawPoint[][], view: PageView) => {
      // Each finished stroke is its own undo step while the pencil is armed;
      // they collapse to one mark when the drawing commits (see `commitDrawing`).
      snapshotBefore(null);

      const next: DrawBuffer = { ...drawBuffer.current };
      if (strokes.length === 0) delete next[pageIndex];
      else next[pageIndex] = { strokes, view };

      drawBuffer.current = next;
      setDrawState(next);
    },
    [snapshotBefore],
  );

  /**
   * Turn the accumulated strokes into `draw` fields and empty the buffer.
   *
   * Called whenever the pencil is put down — switching tools, Escape, or Save —
   * because a drawing commits as one mark rather than per stroke. Returns the
   * fields it made so `save` can burn them without waiting for `setFields`.
   */
  const commitDrawing = useCallback((): SignField[] => {
    const drawn = fieldsFromDrawBuffer(drawBuffer.current);

    drawBuffer.current = {};
    setDrawState({});
    if (drawn.length > 0) setFields((current) => [...current, ...drawn]);

    // The strokes of this pencil session each pushed an undo step; once they
    // commit to a mark, that mark undoes in one, like a signature. Collapse
    // them to the single pre-drawing snapshot — kept only if a mark was made.
    const base = drawSessionBase.current;
    if (base !== null) {
      setUndoStack((stack) => stack.slice(0, drawn.length > 0 ? base + 1 : base));
      drawSessionBase.current = null;
    }
    editKey.current = null;

    return drawn;
  }, []);

  const chooseTool = useCallback(
    (next: SignFieldKind | undefined) => {
      // Leaving the pencil commits whatever was drawn. The other tools disarm
      // themselves on placement and have nothing pending.
      if (toolRef.current === 'draw' && next !== 'draw') commitDrawing();
      // Arming it opens a drawing session, whose per-stroke undo steps start
      // here and collapse to one on commit.
      else if (next === 'draw' && toolRef.current !== 'draw') {
        drawSessionBase.current = undoStackRef.current.length;
      }
      setTool(next);
      setError(undefined);
    },
    [commitDrawing],
  );

  const place = useCallback(
    (field: SignField) => {
      snapshotBefore(null);
      setFields((current) => [...current, field]);
      // Placing a field disarms the tool. A second signature or text box is a
      // second trip through the toolbar's toggle — see `PdfViewer`'s comment
      // above the two tool buttons for why. The pencil is the exception and
      // never reaches here — it stays armed and accumulates via `setDrawStrokes`.
      setTool(undefined);
    },
    [snapshotBefore],
  );

  const move = useCallback(
    (id: string, rect: FieldRect) => {
      // One undo step per drag, not per frame: the first move of a drag records
      // the state before it, the rest coalesce, and `endEdit` closes the drag.
      snapshotBefore(`move:${id}`);
      setFields((current) =>
        current.map((field) => (field.id === id ? { ...field, rect } : field)),
      );
    },
    [snapshotBefore],
  );

  const setText = useCallback(
    (id: string, text: string) => {
      // A run of typing in one field is one undo step; a different field, or a
      // return to this one after `endEdit`, starts another.
      snapshotBefore(`text:${id}`);
      setFields((current) =>
        current.map((field) => (field.id === id ? { ...field, text } : field)),
      );
    },
    [snapshotBefore],
  );

  const remove = useCallback(
    (id: string) => {
      snapshotBefore(null);
      setFields((current) => current.filter((field) => field.id !== id));
    },
    [snapshotBefore],
  );

  const undo = useCallback(() => {
    const stack = undoStackRef.current;
    const snap = stack[stack.length - 1];
    if (snap === undefined) return;

    setFields(snap.fields);
    drawBuffer.current = snap.draw;
    setDrawState(snap.draw);
    // Remount the draw layers so an undone stroke actually leaves the canvas.
    setDrawGeneration((generation) => generation + 1);
    editKey.current = null;
    setUndoStack(stack.slice(0, -1));
  }, []);

  const name = signedName(file.name);

  const save = useCallback(() => {
    if (blob === undefined || saving) return;

    // Fold any in-progress drawing into the fields first — a drawing commits on
    // Save as it does on a tool switch. `commitDrawing` returns the new fields
    // because `setFields` has not flushed yet.
    const drawn = commitDrawing();
    const allFields = [...fields, ...drawn];
    // Nothing placed and nothing drawn. Save should not have been offered, but
    // an empty run would only earn a `no-fields` refusal.
    if (allFields.length === 0) return;

    async function run(bytes: Blob): Promise<void> {
      setSaving(true);
      setError(undefined);

      try {
        const signed = await signPdf({
          // A fresh copy each time: `PDFDocument.load` reads it, and the cache
          // holds the one `Blob` the viewer is also rendering from.
          bytes: await bytes.arrayBuffer(),
          fields: allFields,
          // Undefined when the signer never set a mark up and only drew — each
          // drawing carries its own ink, so the account signature is optional.
          ink,
        });

        await uploadFile(
          new File([signed as BlobPart], name, { type: 'application/pdf' }),
          { spaceId, folderId },
          // FIL-8c — the source, resolved and recorded by the API rather than
          // trusted. `lib/upload.ts` carries it to the finalize.
          { signedFromFileId: file.id },
        );

        setFields([]);
        setTool(undefined);
        setUndoStack([]);
        drawSessionBase.current = null;
        editKey.current = null;
        onSigned();
      } catch (caught) {
        if (caught instanceof SignError) {
          setError(SIGNING_COPY.errors[caught.reason]);
        } else {
          // The API's own words wherever there are any — a 403 on a
          // observer space, a 429 naming a cap, are things this screen could
          // not work out for itself.
          setError(messageFrom(caught, SIGNING_COPY.errors.upload));
        }
      } finally {
        setSaving(false);
      }
    }

    void run(blob);
  }, [blob, ink, saving, fields, name, spaceId, folderId, file.id, onSigned, commitDrawing]);

  // A placed field, or a drawing not yet switched away from. Both are work the
  // reader would lose on close, so both gate the prompts and the Save control.
  const hasContent = fields.length > 0 || Object.keys(drawStrokes).length > 0;

  const clearDrawings = useCallback(() => {
    drawBuffer.current = {};
    setDrawState({});
  }, []);

  const requestClose = useCallback(
    (close: () => void) => () => {
      // `confirm` rather than a dialog, and it is the same call `FolderView`
      // declined to build a component for: one caller, and a second modal on
      // top of a `role="dialog"` is its own accessibility problem.
      if (hasContent && !window.confirm(SIGNING_COPY.discard.prompt)) return;
      close();
    },
    [hasContent],
  );

  const discard = useCallback(() => {
    // Confirmed only when something was actually placed or drawn. Cancelling a
    // merely armed tool costs nothing, and a prompt for it would train the
    // reader to dismiss the prompt that does matter.
    if (hasContent && !window.confirm(SIGNING_COPY.discard.cancelPrompt)) return;

    setFields([]);
    clearDrawings();
    setTool(undefined);
    setError(undefined);
    setUndoStack([]);
    drawSessionBase.current = null;
    editKey.current = null;
  }, [hasContent, clearDrawings]);

  const onEscape = useCallback(() => {
    // Escape backs out of the innermost thing first: the armed tool, then the
    // reader. `ViewerFrame` already applies that rule to its overflow menu.
    if (tool === undefined) return false;

    // Putting the pencil down commits what it drew, as a tool switch would.
    if (tool === 'draw') commitDrawing();
    setTool(undefined);
    return true;
  }, [tool, commitDrawing]);

  return {
    signable: enabled && ink !== undefined,
    needsSignature: enabled && loaded && ink === undefined,
    canDraw: enabled,
    ink,
    tool,
    fields,
    hasContent,
    drawStrokes,
    drawGeneration,
    saving,
    error,
    chooseTool,
    place,
    setDrawStrokes,
    move,
    setText,
    remove,
    endEdit,
    canUndo: undoStack.length > 0,
    undo,
    save,
    discard,
    signedName: name,
    requestClose,
    onEscape,
  };
}
